import { kWeightingFilters } from './loudness';

/**
 * Loudness while a mix plays.
 *
 * The offline measurement in `loudness.ts` has all the audio in front of it; a
 * live meter is handed a hundred milliseconds at a time and has to keep the
 * running state itself. This class is that state, and it is deliberately pure:
 * it takes blocks of samples and returns numbers, with no audio context
 * anywhere, so what the meter reports can be tested against the offline
 * measurement it is supposed to agree with.
 *
 * Two things carry across block boundaries and would be wrong if they did not:
 *
 * - **The filter state.** Restarting the K-weighting filters every block would
 *   put a transient at every boundary, ten times a second.
 * - **The mean squares of past blocks.** Momentary loudness is a 400ms window
 *   and short-term a 3s one; both are assembled from the 100ms pieces already
 *   seen, and the integrated figure gates over all of them.
 */

/** Loudness is accumulated in pieces of this length and combined afterwards. */
const PIECE_SEC = 0.1;
const MOMENTARY_PIECES = 4; // 400ms
const SHORT_TERM_PIECES = 30; // 3s

const LOUDNESS_OFFSET = -0.691;
const ABSOLUTE_GATE_LUFS = -70;
const RELATIVE_GATE_LU = -10;

export interface LiveLoudness {
  /** Loudness of the last 400ms, in LUFS. */
  momentaryLufs: number;
  /** Loudness of the last three seconds, in LUFS. */
  shortTermLufs: number;
  /** Gated loudness of everything measured since the last reset, in LUFS. */
  integratedLufs: number;
  /** Highest sample seen since the last reset, in dB relative to full scale. */
  peakDb: number;
}

interface FilterState {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

const freshState = (): FilterState => ({ x1: 0, x2: 0, y1: 0, y2: 0 });

export class LoudnessMeter {
  private readonly shelf;
  private readonly highPass;
  private states: FilterState[][] = [];

  /** Mean square of each completed 100ms piece, per channel, summed and weighted. */
  private pieces: number[] = [];
  private pieceSum = 0;
  private pieceSamples = 0;
  private readonly pieceLength: number;
  private peak = 0;

  constructor(sampleRate: number) {
    const [shelf, highPass] = kWeightingFilters(sampleRate);
    this.shelf = shelf;
    this.highPass = highPass;
    this.pieceLength = Math.max(1, Math.round(PIECE_SEC * sampleRate));
  }

  /** Forgets everything, as pressing stop should. */
  reset(): void {
    this.states = [];
    this.pieces = [];
    this.pieceSum = 0;
    this.pieceSamples = 0;
    this.peak = 0;
  }

  /**
   * Feeds one block of audio, as the worklet delivers it: one array per channel,
   * all the same length.
   */
  push(channels: readonly Float32Array[]): void {
    const length = channels[0]?.length ?? 0;
    if (length === 0) return;

    while (this.states.length < channels.length) this.states.push([freshState(), freshState()]);

    for (let i = 0; i < length; i++) {
      let sum = 0;
      for (let c = 0; c < channels.length; c++) {
        const raw = channels[c][i];
        const magnitude = Math.abs(raw);
        if (magnitude > this.peak) this.peak = magnitude;
        const weighted = this.filterSample(c, raw);
        sum += weighted * weighted;
      }
      this.pieceSum += sum;
      this.pieceSamples++;

      if (this.pieceSamples >= this.pieceLength) {
        this.pieces.push(this.pieceSum / this.pieceSamples);
        this.pieceSum = 0;
        this.pieceSamples = 0;
        // The integrated figure needs every piece, but nothing needs more than
        // an hour of them; beyond that the oldest are dropped rather than let
        // a long session grow without bound.
        if (this.pieces.length > 36000) this.pieces.shift();
      }
    }
  }

  /** What the meter currently reads. */
  read(): LiveLoudness {
    return {
      momentaryLufs: this.windowLoudness(MOMENTARY_PIECES),
      shortTermLufs: this.windowLoudness(SHORT_TERM_PIECES),
      integratedLufs: this.integrated(),
      peakDb: this.peak > 0 ? 20 * Math.log10(this.peak) : -Infinity,
    };
  }

  private filterSample(channel: number, value: number): number {
    const [shelfState, hpState] = this.states[channel];
    const afterShelf = runBiquad(this.shelf, shelfState, value);
    return runBiquad(this.highPass, hpState, afterShelf);
  }

  /** Loudness of the last `count` pieces, or of everything if fewer exist. */
  private windowLoudness(count: number): number {
    if (this.pieces.length === 0) return -Infinity;
    const from = Math.max(0, this.pieces.length - count);
    let sum = 0;
    for (let i = from; i < this.pieces.length; i++) sum += this.pieces[i];
    const mean = sum / (this.pieces.length - from);
    return mean <= 0 ? -Infinity : LOUDNESS_OFFSET + 10 * Math.log10(mean);
  }

  /**
   * Integrated loudness, gated exactly as the offline measurement gates it —
   * except that the blocks are assembled here from 100ms pieces rather than
   * taken from a finished file.
   */
  private integrated(): number {
    const blocks: number[] = [];
    for (let start = 0; start + MOMENTARY_PIECES <= this.pieces.length; start++) {
      let sum = 0;
      for (let i = start; i < start + MOMENTARY_PIECES; i++) sum += this.pieces[i];
      const mean = sum / MOMENTARY_PIECES;
      if (mean > 0) blocks.push(LOUDNESS_OFFSET + 10 * Math.log10(mean));
    }
    if (blocks.length === 0) return -Infinity;

    const aboveAbsolute = blocks.filter((value) => value > ABSOLUTE_GATE_LUFS);
    if (aboveAbsolute.length === 0) return -Infinity;
    const ungated = meanLoudness(aboveAbsolute);
    const gated = aboveAbsolute.filter((value) => value > ungated + RELATIVE_GATE_LU);
    return gated.length === 0 ? ungated : meanLoudness(gated);
  }
}

function runBiquad(
  filter: { b0: number; b1: number; b2: number; a1: number; a2: number },
  state: FilterState,
  x0: number,
): number {
  const y0 =
    filter.b0 * x0 + filter.b1 * state.x1 + filter.b2 * state.x2 - filter.a1 * state.y1 - filter.a2 * state.y2;
  state.x2 = state.x1;
  state.x1 = x0;
  state.y2 = state.y1;
  state.y1 = y0;
  return y0;
}

function meanLoudness(values: readonly number[]): number {
  let sum = 0;
  for (const value of values) sum += Math.pow(10, (value - LOUDNESS_OFFSET) / 10);
  return sum <= 0 ? -Infinity : LOUDNESS_OFFSET + 10 * Math.log10(sum / values.length);
}
